import EditorModule from "../EditorModule";
import { getProgress, reportWorkSeconds } from "api-operations/editor/editor";
import { removeCaretGuards } from "editorNextGen/AssetElement";
import i18next from "i18next";
import { HTTPLogger } from "logger/HTTPLoggerStatic";
import { restoreButton as restoreButtonIcon } from "editorNextGen/icons/restore";
import { calculateProgress, consumeEvent, sameAsset } from "editorNextGen/utils";
import { secondsToReadableUnits } from "helpers/utils";
import "./style.scss";
import UserService from "UserService";
const LOG_TIME_INTERVAL = 30;
export default class ContentEditionModule extends EditorModule {
    constructor(editor) {
        super(editor);
        this.moduleId = "ContentEditionModule";
        this.assignmentProgressBar = document.createElement("progress");
        this.sectionProgressBar = document.createElement("progress");
        this.workTimeInterval = null;
        this.duplicateUpdateTimeouts = new Map();
        this._assetEdition = true;
        this.focused = true;
        this.completitionPreconditions = [(assetElement) => Promise.resolve(true)];
        this.completingAssets = new Set();
        this._completeWithPreconditions = (assetElement) => Promise.all(this.completitionPreconditions.map((precondition) => precondition(assetElement))).then((preconditionResults) => {
            var _a, _b;
            const allPassed = preconditionResults.every(result => result === true);
            if (allPassed) {
                HTTPLogger.info('User - Complete preconditions passed', {
                    assetId: assetElement.assetId,
                    order: assetElement.order,
                    assignmentId: assetElement.assignmentId,
                    preconditionResults
                });
                assetElement.complete();
            }
            else {
                HTTPLogger.warn('User - Complete blocked by preconditions', {
                    assetId: assetElement.assetId,
                    order: assetElement.order,
                    assignmentId: assetElement.assignmentId,
                    preconditionResults,
                    doneDisabled: (_b = (_a = this.getDoneButton(assetElement)) === null || _a === void 0 ? void 0 : _a.disabled) !== null && _b !== void 0 ? _b : null
                });
            }
        }).catch((error) => {
            HTTPLogger.error('User - Complete preconditions failed', error, {
                assetId: assetElement.assetId,
                order: assetElement.order,
                assignmentId: assetElement.assignmentId
            });
        }).finally(() => {
            this.releaseCompleting(assetElement, 'preconditions-finished');
        });
        this.isCompletable = (asset) => this.editor.modules.every(m => m.assetCompletable(asset));
        this.getRestoreButton = (asset) => asset.querySelector("button.restore");
        this.getDoneButton = (asset) => asset.querySelector("button.done");
        const { statusBar, sectionHeader, sectionBar } = editor;
        this.statusBarAssignmentAssetsRemaining = statusBar
            .createStatusBarSection(`${i18next.t('editorStatusBar.remainingAssets')}: `);
        this.statusBarAssignmentTimeRemaining = statusBar
            .createStatusBarSection(`${i18next.t('editorStatusBar.estTimeRemaining')}: `);
        this.statusBarSectionAssetsTotal = statusBar
            .createStatusBarSection(`${i18next.t('editorStatusBar.totalAssetsSection')}: `, true);
        this.statusBarSectionAssetsRemaining = statusBar
            .createStatusBarSection(`${i18next.t('editorStatusBar.remainingAssetsSection')}: `, true);
        this.assignmentProgressBar.max = 1;
        this.sectionProgressBar.max = 1;
        this.editor.header.appendChild(this.assignmentProgressBar);
        document.querySelector('xfl-new-editor-section-header')
            ? sectionHeader.appendChild(this.sectionProgressBar)
            : sectionBar.appendChild(this.sectionProgressBar);
        window.addEventListener('blur', () => { this.focused = false; });
        window.addEventListener('focus', () => { this.focused = true; });
    }
    unload() {
        this.editor.content.contentEditable = "false";
        this.clearWorkTimeInterval();
        Array.from(this.duplicateUpdateTimeouts.values()).forEach(window.clearInterval);
        this.disableAssetsEdition();
        this.assignmentProgressBar.remove();
        this.sectionProgressBar.remove();
        this.editor.modules = this.editor.modules.filter(m => m != this);
    }
    disableAssetsEdition() {
        this.editor.content.querySelectorAll("xfl-new-editor-asset").forEach((a) => {
            var _a, _b;
            a.contentDiv.contentEditable = "false";
            (_a = a.querySelector("button.edit")) === null || _a === void 0 ? void 0 : _a.remove();
            (_b = this.getDoneButton(a)) === null || _b === void 0 ? void 0 : _b.remove();
        });
    }
    finishLoading() {
        var _a;
        this.setWorkTimeInterval();
        this.updateProgress(undefined, [(_a = this.editor.section) === null || _a === void 0 ? void 0 : _a.id]);
        this.editor.content.contentEditable = "true";
        this.editor.placeCursorInActiveAsset();
    }
    clearWorkTimeInterval() {
        this.workTimeInterval && window.clearInterval(this.workTimeInterval);
    }
    setWorkTimeInterval() {
        this.workTimeInterval = window.setInterval(this.reportWorkTime.bind(this), LOG_TIME_INTERVAL * 1000);
    }
    input() {
        const { activeAsset } = this.editor;
        if (!activeAsset) {
            return;
        }
        this.contentChanged(activeAsset);
        this.updateDoneButton(activeAsset);
        this.updateRestoreButton(activeAsset);
        const { assetId } = activeAsset;
        if (this.duplicateUpdateTimeouts.has(assetId)) {
            clearTimeout(this.duplicateUpdateTimeouts.get(assetId));
        }
        const duplicates = this.editor.getAssetDuplicates(activeAsset);
        this.duplicateUpdateTimeouts.set(assetId, window.setTimeout(() => {
            duplicates.forEach((a) => { a.currentText = activeAsset.currentText; });
            this.duplicateUpdateTimeouts.delete(assetId);
        }, duplicates.length * 10)); // Timeout duration depends on how much copies this asset has
    }
    isRecycled(assetElement) {
        return !assetElement.paid && assetElement.locked;
    }
    assetCreated(assetElement, asset = undefined) {
        if (assetElement.translationLevel == "NO_TRANSLATION") {
            return;
        }
        if (this._assetEdition) {
            const recycledEditLabel = document.createElement("span");
            recycledEditLabel.textContent = i18next.t("editor.recycledHint");
            recycledEditLabel.className = "recycledHint";
            this.isRecycled(assetElement) && assetElement.iconBar.appendChild(recycledEditLabel);
            const editButton = document.createElement("button");
            editButton.innerHTML = i18next.t("editor.edit");
            editButton.className = "edit";
            editButton.addEventListener("click", assetElement.unlockClicked.bind(assetElement));
            assetElement.iconBar.appendChild(editButton);
            const restoreButton = document.createElement("button");
            restoreButton.innerHTML = restoreButtonIcon;
            restoreButton.className = "restore";
            restoreButton.addEventListener("click", assetElement.restoreClicked.bind(assetElement));
            restoreButton.addEventListener("mouseenter", (e) => {
                this.getSimpleTooltip().showTooltip(restoreButton, e, 'bottom');
            });
            restoreButton.addEventListener("mouseleave", (e) => {
                this.getSimpleTooltip().hide();
            });
            assetElement.iconBar.appendChild(restoreButton);
            const doneButton = document.createElement("button");
            doneButton.textContent = i18next.t("editor.complete");
            doneButton.className = "done";
            doneButton.addEventListener("click", (event) => {
                consumeEvent(event);
                this.completeWithPreconditions(assetElement, true, 'click');
            });
            assetElement.iconBar.appendChild(doneButton);
        }
    }
    completeWithPreconditions(assetElement, completed, trigger = 'click') {
        var _a;
        const diagnostics = this.completeDiagnostics(assetElement, trigger);
        // Prevent double-triggering (e.g., from pressing CTRL+Enter twice)
        if (this.completingAssets.has(assetElement.assetId)) {
            HTTPLogger.warn('User - Complete ignored, asset already completing', diagnostics);
            return;
        }
        if (!completed) {
            HTTPLogger.info('User - Complete unmark', diagnostics);
            assetElement.complete();
            return;
        }
        HTTPLogger.info('User - Complete attempted', diagnostics);
        // Mark asset as being completed
        this.completingAssets.add(assetElement.assetId);
        this.updateAssetContentEditable(assetElement);
        this.updateDoneButton(assetElement);
        try {
            const usedTags = assetElement.getUsedTagsQuality();
            const tagsCount = (_a = assetElement.format) === null || _a === void 0 ? void 0 : _a.tagsCount;
            const missingFormatting = usedTags !== tagsCount && !!tagsCount;
            if (this.editor.assetMissingFormattingModal && missingFormatting) {
                HTTPLogger.warn('User - Complete blocked, missing formatting modal', {
                    ...diagnostics,
                    usedTags,
                    tagsCount
                });
                this.editor.assetMissingFormattingModal.callback = () => this._completeWithPreconditions(assetElement);
                this.editor.assetMissingFormattingModal.onCancel = () => this.releaseCompleting(assetElement, 'missing-formatting-cancelled');
                this.editor.assetMissingFormattingModal.setOpen();
                return;
            }
            this._completeWithPreconditions(assetElement);
        }
        catch (error) {
            this.releaseCompleting(assetElement, 'complete-threw');
            HTTPLogger.error('User - Complete failed before preconditions', error, diagnostics);
        }
    }
    releaseCompleting(assetElement, reason) {
        this.completingAssets.delete(assetElement.assetId);
        this.updateAssetContentEditable(assetElement);
        this.updateDoneButton(assetElement);
        HTTPLogger.info('User - Complete lock released', {
            assetId: assetElement.assetId,
            order: assetElement.order,
            assignmentId: assetElement.assignmentId,
            reason,
            contentEditable: assetElement.contentDiv.contentEditable
        });
    }
    completeDiagnostics(assetElement, trigger) {
        var _a, _b, _c, _d, _e;
        const doneButton = this.getDoneButton(assetElement);
        return {
            assetId: assetElement.assetId,
            order: assetElement.order,
            assignmentId: assetElement.assignmentId,
            trigger,
            completed: assetElement.completed,
            locked: assetElement.locked,
            modified: assetElement.modified,
            alreadyCompleting: this.completingAssets.has(assetElement.assetId),
            contentEditable: assetElement.contentDiv.contentEditable,
            doneDisabled: (_a = doneButton === null || doneButton === void 0 ? void 0 : doneButton.disabled) !== null && _a !== void 0 ? _a : null,
            currentText: assetElement.currentText,
            currentPlainText: assetElement.currentPlainText,
            usedTags: assetElement.getUsedTagsQuality(),
            tagsCount: (_c = (_b = assetElement.format) === null || _b === void 0 ? void 0 : _b.tagsCount) !== null && _c !== void 0 ? _c : null,
            formatTexts: (_e = (_d = assetElement.format) === null || _d === void 0 ? void 0 : _d.formatTexts) !== null && _e !== void 0 ? _e : null,
            translationLevel: assetElement.translationLevel,
            dragAndDropMode: assetElement.dragAndDropMode
        };
    }
    getSimpleTooltip() {
        const simpleTooltip = document.querySelector("xfl-new-popup-ele");
        simpleTooltip.content = i18next.t("editor.restore");
        return simpleTooltip;
    }
    assetActivationChanged(asset, active) {
        if (active) {
            this.editor.commitTimeouts.get(asset.assetId) && this.editor.commit(asset);
            this.updateDoneButton(asset);
            this.updateRestoreButton(asset);
        }
    }
    assetCompletionChanged(asset, completed, onCommitCompleted) {
        this.editor.commit(asset)
            .then(() => { this.updateProgress(undefined, asset.sections); })
            .then(() => { if (completed && onCommitCompleted)
            onCommitCompleted(); })
            .catch(error => this.editor.setState({ error }));
    }
    assetCompletabilityChanged(asset) {
        this.updateDoneButton(asset);
    }
    updateDoneButton(asset) {
        const doneButton = this.getDoneButton(asset);
        if (!doneButton)
            return;
        const isBeingCompleted = this.completingAssets.has(asset.assetId);
        doneButton.disabled = isBeingCompleted || !this.isCompletable(asset);
    }
    assetInstanceLockChanged(asset, locked) {
        this.updateAssetContentEditable(asset);
    }
    assetInstanceCompletionChanged(asset, completed) {
        this.updateAssetContentEditable(asset);
        this.updateRestoreButton(asset);
    }
    updateRestoreButton(asset) {
        const restoreButton = this.getRestoreButton(asset);
        if (restoreButton) {
            restoreButton.style.display = (asset.modified && !asset.completed) ? "block" : "none";
        }
    }
    keyDownComplete(event) {
        const onCommitCompleted = () => {
            this.editor.jumpToNextUnfinishedAsset();
            this.editor.placeCursorInActiveAsset();
        };
        const { activeAsset } = this.editor;
        switch (event.key) {
            case "Enter":
                if ((event.ctrlKey || event.metaKey) && activeAsset) {
                    // Check if done button is disabled (e.g., during spellchecking)
                    const doneButton = this.getDoneButton(activeAsset);
                    if (doneButton === null || doneButton === void 0 ? void 0 : doneButton.disabled) {
                        HTTPLogger.warn('User - Complete ignored, done button disabled', this.completeDiagnostics(activeAsset, 'keyboard'));
                        consumeEvent(event);
                        return;
                    }
                    if (!activeAsset.paid && activeAsset.locked) {
                        activeAsset.locked = false;
                        this.editor.placeCursorInActiveAsset();
                    }
                    else {
                        if (this.editor.isTrustMiningStage && activeAsset.assetError && !activeAsset.completed) {
                            if (activeAsset.assetErrorType) {
                                this.completeWithPreconditions(activeAsset, !activeAsset.completed, 'keyboard');
                            }
                        }
                        else if (activeAsset.translationLevel != 'NO_TRANSLATION') {
                            this.completeWithPreconditions(activeAsset, !activeAsset.completed, 'keyboard');
                        }
                        if (activeAsset.translationLevel == 'NO_TRANSLATION') {
                            onCommitCompleted();
                        }
                    }
                    consumeEvent(event);
                }
                break;
        }
    }
    assetChanged(asset) {
        const offset = findOffsetInRoot(asset.contentDiv);
        this.editor.observer.disconnect();
        asset.currentText = asset.currentText;
        this.editor.observer.observe(asset.contentDiv, { subtree: true, characterData: true });
        const [x, y] = findOffsetInChildNodeAtTextIndex(asset.contentDiv, offset);
        setRange(y, x);
    }
    keyPress(event) {
        if (!this.isSelectionOverwritable()) {
            consumeEvent(event);
            return;
        }
        // Allow Enter key to work naturally in contenteditable divs, enabling multi-line assets.
        // The browser will insert <br>, <div>, or <p> tags depending on the browser.
        // These tags are sanitized/preserved in getSanitizedHTML() and converted to \n in getNodesToText().
    }
    keyUp(event) {
        const range = window.getSelection().getRangeAt(0);
        try { // Firefox problem workaround
            range.startContainer.parentNode;
        }
        catch (error) {
            switch (event.key) {
                case "ArrowDown":
                    this.editor.jumpBy(1);
                    this.editor.placeCursorInActiveAsset();
                    break;
                case "ArrowUp":
                    this.editor.jumpBy(-1);
                    this.editor.placeCursorInActiveAsset(true);
            }
        }
        this.editor.updateSelectionRange();
    }
    clipboardEvent(event) {
        var _a;
        if (!this.isSelectionOverwritable()) {
            consumeEvent(event);
            return;
        }
        const { editor } = this;
        if (!editor.activeAsset)
            return;
        if (event.type === "paste" || event.type === "copy" || event.type === "cut") {
            HTTPLogger.externalAssetModification({
                type: event.type,
                assignmentId: editor.assignmentId,
                stage: editor.stage,
                totalAssets: editor.statusBar.totalAssets,
                translationLevel: editor.activeAsset.translationLevel,
                assetLength: (_a = editor.activeAsset) === null || _a === void 0 ? void 0 : _a.currentPlainText.length,
                username: UserService.getTokenUserInfo().login,
                sourceLanguage: editor.sourceLanguage,
                targetLanguage: editor.targetLanguage
            });
        }
    }
    isSelectionOverwritable() {
        const selection = window.getSelection();
        return !selection
            || selection.rangeCount == 1
                && sameAsset(selection.getRangeAt(0).startContainer, selection.getRangeAt(0).endContainer);
    }
    updateAssetContentEditable(asset) {
        const isBeingCompleted = this.completingAssets.has(asset.assetId);
        asset.contentDiv.contentEditable = ((asset.paid || !asset.locked) && !asset.completed && !isBeingCompleted).toString();
        asset.refreshFormattingEditability();
    }
    contentChanged(asset) {
        this.editor.clearCommitTimeout(asset);
        this.editor.commitTimeouts.set(asset.assetId, window.setTimeout(() => this.editor.commit(asset), 5000));
    }
    async updateProgress(editorProgess = undefined, sections) {
        const { assignmentId } = this.editor;
        try {
            const progress = editorProgess || await getProgress(assignmentId);
            const assignmentProgress = calculateProgress(progress.total, progress.completed);
            this.remainingAssignmentAssets = progress.total - progress.completed;
            this.assignmentProgressBar.value = assignmentProgress;
            this.assignmentTimeEstimated = progress.secondsRemaining;
            this.editor.modules.forEach(m => m.progressChanged(assignmentProgress));
            sections.filter((s) => !!s).forEach(sectionId => {
                var _a;
                const sectionProgress = (_a = progress.jobSections) === null || _a === void 0 ? void 0 : _a[sectionId];
                if (sectionProgress)
                    this.applySectionProgress(sectionId, sectionProgress);
            });
        }
        catch (error) {
            this.editor.setState({ error });
            HTTPLogger.error('System - Failed to update progress', error);
        }
    }
    applySectionProgress(sectionId, progress) {
        const assetProgress = calculateProgress(progress.total, progress.completed);
        this.remainingSectionAssets = progress.total - progress.completed;
        this.totalSectionAssets = progress.total;
        this.sectionProgressBar.value = assetProgress;
        this.editor.updateSectionProgress(sectionId, progress.total, progress.completed);
    }
    async reportWorkTime() {
        if (document.visibilityState == "hidden" || !this.focused) {
            return;
        }
        try {
            await reportWorkSeconds(this.editor.assignmentId, LOG_TIME_INTERVAL);
        }
        catch (error) {
            this.editor.setState({ error });
            HTTPLogger.error('System - Failed to report work time', error);
        }
    }
    get remainingAssignmentAssets() {
        return parseInt(this.statusBarAssignmentAssetsRemaining.textContent || "");
    }
    set remainingAssignmentAssets(assets) {
        this.statusBarAssignmentAssetsRemaining.textContent = assets.toString();
    }
    get assignmentTimeEstimated() {
        return Number(this.statusBarAssignmentTimeRemaining.dataset.seconds);
    }
    set assignmentTimeEstimated(duration) {
        this.statusBarAssignmentTimeRemaining.dataset.seconds = `${duration}` || undefined;
        this.statusBarAssignmentTimeRemaining.textContent = secondsToReadableUnits(duration || 0);
    }
    get remainingSectionAssets() {
        return parseInt(this.statusBarSectionAssetsRemaining.textContent || "");
    }
    set remainingSectionAssets(assets) {
        this.statusBarSectionAssetsRemaining.textContent = assets.toString();
    }
    get totalSectionAssets() {
        return parseInt(this.statusBarSectionAssetsTotal.textContent || "");
    }
    set totalSectionAssets(assets) {
        this.statusBarSectionAssetsTotal.textContent = assets.toString();
    }
    set assetEdition(edition) {
        this._assetEdition = edition;
    }
}
function findOffsetInRoot(root) {
    const selection = window.getSelection();
    if (selection && (selection === null || selection === void 0 ? void 0 : selection.focusNode)) {
        const range = document.createRange();
        range.setStart(root, 0);
        range.setEnd(selection === null || selection === void 0 ? void 0 : selection.focusNode, selection === null || selection === void 0 ? void 0 : selection.focusOffset);
        return removeCaretGuards(range.toString()).length + 1; // +1 for carret guard at the begining of text
    }
    return 0;
}
function setRange(newStartNode, newRangeStartOffset) {
    const range = document.createRange();
    range.setStart(newStartNode, newRangeStartOffset);
    const selection = window.getSelection();
    selection === null || selection === void 0 ? void 0 : selection.removeAllRanges();
    selection === null || selection === void 0 ? void 0 : selection.addRange(range);
}
function findOffsetInChildNodeAtTextIndex(node, index) {
    var _a, _b, _c;
    if (index == 0)
        return [0, node];
    var sum = 0;
    const nodes = getAllNestedNodes(Array.from(node.childNodes))
        .flat(20)
        .filter((node) => node instanceof Text);
    for (var i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        sum = sum + (((_a = n.textContent) === null || _a === void 0 ? void 0 : _a.length) || 0);
        // textNode = n
        if (sum >= index) {
            const range = document.createRange();
            range.setStart(n, (((_b = n.textContent) === null || _b === void 0 ? void 0 : _b.length) || 0) - (sum - index));
            return [(((_c = n.textContent) === null || _c === void 0 ? void 0 : _c.length) || 0) - (sum - index), n];
        }
    }
    return [0, node];
}
function getAllNestedNodes(nodes) {
    return nodes
        .map(node => {
        return node.childNodes.length ? getAllNestedNodes(Array.from(node.childNodes)) : node;
    });
}
